import React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Space, Button, message } from "@mimber/ui-web"
import { Form, InputField, SelectField } from "@mimber/forms"
import { useCreateGroup } from "@mimber/client/mimber-organization"
import { GroupTypeEnum, GroupVisibilityEnum } from "@mimber/schema"
import { GROUP_TYPE_OPTIONS, GROUP_VISIBILITY_OPTIONS } from "@/utils"
import type { CreateGroupFormProps } from "./CreateGroupForm.types"
import {
  createGroupSchema,
  type CreateGroupData,
} from "./CreateGroupForm.schema"

export const CreateGroupForm: React.FunctionComponent<CreateGroupFormProps> = ({
  onSuccess,
}) => {
  const form = useForm<CreateGroupData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(createGroupSchema) as any,
    mode: "onBlur",
    defaultValues: {
      name: "",
      description: "",
      type: GroupTypeEnum.OTHER,
      visibility: GroupVisibilityEnum.PUBLIC,
    },
  })

  const {
    reset,
  } = form

  const mutation = useCreateGroup({
    onSuccess: (response) => {
      message.success("Group created successfully")
      reset() // Reset form to default values
      onSuccess?.(response.data)
    },
    onError: (error) => {
      message.error(`Failed to create group: ${error.message}`)
    },
  })

  const onSubmit = (data: CreateGroupData) => {
    mutation.mutate(data)
  }

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      disabled={mutation.isPending}
      layout="vertical"
    >
      <InputField
        name="name"
        label="Group Name"
        placeholder="e.g., Events Committee, Marketing Team"
        required
        maxLength={255}
        showCount
      />

      <InputField
        name="description"
        label="Description"
        placeholder="Brief description of the group's purpose"
        maxLength={1000}
        showCount
        multiline
        rows={3}
      />

      <SelectField
        name="type"
        label="Type"
        placeholder="Select group type"
        required
        options={GROUP_TYPE_OPTIONS}
        help="Committees are typically formal governance bodies"
      />

      <SelectField
        name="visibility"
        label="Visibility"
        placeholder="Select visibility"
        required
        options={GROUP_VISIBILITY_OPTIONS}
        help="Public groups are visible to all members"
      />

      <Space style={{ marginTop: 16 }}>
        <Button
          type="primary"
          htmlType="submit"
          loading={mutation.isPending}

        >
          Create Group
        </Button>
      </Space>
    </Form>
  )
}
